import React, { useCallback, useEffect, useState } from 'react';
import { getCookie, hasCookie } from 'cookies-next';
import jwtDecode from 'jwt-decode';
import { exit } from '../Intermediary/utilsIntermediary';
import { getDocRapidoQuotasAcquisitionsByConsultant } from '../../utils/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faEye, faFileLines, faPenToSquare, faPlus, faUserPen } from '@fortawesome/free-solid-svg-icons';
import AddRecommendationIntermediary from '../AddRecommendationIntermediary/AddRecommendationIntermediary';
import IntermediaryDetailsModal from '../IntermediaryDetailsModal/IntermediaryDetailsModal';
import IntermediaryDocumentModal from '../IntermediaryDocumentModal/IntermediaryDocumentModal';
import IntermediaryProfileModal from '../IntermediaryProfileModal/IntermediaryProfileModal';

import './Intermediary.css';
import { INTERMEDIARY_TOKEN_COOKIE } from '../../utils/authCookies';

const getLoggedIntermediary = () => {
    const token = getCookie(INTERMEDIARY_TOKEN_COOKIE);

    if (!token) {
        return null;
    }

    try {
        const savedToken = typeof token === 'string' && token.startsWith('{')
            ? JSON.parse(token)
            : token;
        const decodedToken = typeof savedToken === 'string'
            ? jwtDecode(savedToken)
            : jwtDecode(savedToken.token);

        return decodedToken?.intermediaryUser
            || decodedToken?.consultantUser
            || decodedToken?.consultant
            || decodedToken?.referrerUser
            || decodedToken?.user
            || null;
    } catch (error) {
        return null;
    }
};

const formatDate = (date) => {
    if (!date) {
        return '-';
    }

    return new Date(date).toLocaleDateString('pt-BR');
};

const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') {
        return '-';
    }

    const numericValue = Number(String(value).replace(',', '.'));

    if (Number.isNaN(numericValue)) {
        return '-';
    }

    return numericValue.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
    });
};

const getFirstQuota = (acquisition) => {
    return acquisition.quotas?.[0] || {};
};

const getInitials = (name) => {
    if (!name) {
        return 'I';
    }

    return name
        .trim()
        .split(' ')
        .slice(0, 2)
        .map((word) => word[0])
        .join('')
        .toUpperCase();
};

const formatStatus = (status) => {
    if (!status) {
        return 'Aguardando';
    }

    return String(status)
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

export default function Intermediary() {
    const [data, setData] = useState([]);
    const [referrerUserName, setReferrerUserName] = useState('');
    const [loggedIntermediaryId, setLoggedIntermediaryId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [modalIntermediaryOpen, setModalIntermediaryOpen] = useState(false);
    const [editAcquisition, setEditAcquisition] = useState(null);
    const [profileModalOpen, setProfileModalOpen] = useState(false);
    const [detailsAcquisition, setDetailsAcquisition] = useState(null);
    const [documentAcquisition, setDocumentAcquisition] = useState(null);
    const [expandedAcquisitionId, setExpandedAcquisitionId] = useState(null);

    const loadAcquisitions = useCallback(async () => {
        setLoading(true);

        if (!hasCookie(INTERMEDIARY_TOKEN_COOKIE)) {
            setLoading(false);
            return;
        }

        try {
            const loggedIntermediary = getLoggedIntermediary();

            if (!loggedIntermediary?.id) {
                setData([]);
                setLoading(false);
                return;
            }

            setReferrerUserName(loggedIntermediary.name || '');
            setLoggedIntermediaryId(loggedIntermediary.id);

            const acquisitions = await getDocRapidoQuotasAcquisitionsByConsultant(loggedIntermediary.id);
            setData(Array.isArray(acquisitions) ? acquisitions : []);
        } catch (error) {
            setData([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadAcquisitions();

        window.addEventListener('intermediary:load-acquisitions', loadAcquisitions);

        return () => {
            window.removeEventListener('intermediary:load-acquisitions', loadAcquisitions);
        };
    }, [loadAcquisitions]);

    const openModalRegisterIntermediary = () => {
        setEditAcquisition(null);
        setModalIntermediaryOpen(true);
    };

    const closeModalRegisterPPR = () => {
        setModalIntermediaryOpen(false);
        setEditAcquisition(null);
        loadAcquisitions();
    };

    const openEditModal = (acquisition) => {
        setEditAcquisition(acquisition);
        setModalIntermediaryOpen(true);
    };

    const toggleQuotas = (acquisitionId) => {
        setExpandedAcquisitionId((currentId) => currentId === acquisitionId ? null : acquisitionId);
    };

    const closeDetailsModal = () => {
        setDetailsAcquisition(null);
    };

    const closeDocumentModal = () => {
        setDocumentAcquisition(null);
    };

    const handleProfileUpdated = (profileData) => {
        setReferrerUserName(profileData.name || '');
    };

    return (
        <div className='h-lg:pb-32 hide h-sm:z-10 h-sm:pt-9 h-sm:pb-9' id='intermediary'>
            <div className='bg-white mx-16 my-8 rounded shadow-2xl px-8 h-sm:mx-2 h-sm:px-2 h-full'>
                <div className='intermediary-header'>
                    <div className='intermediary-user'>
                        <div className='intermediary-avatar'>{getInitials(referrerUserName)}</div>
                        <div>
                            <span className='intermediary-eyebrow'>Bem-vindo</span>
                            <h2>{referrerUserName || 'Intermediario'}</h2>
                            <p>{data.length} {data.length === 1 ? 'lançamento encontrado' : 'lançamentos encontrados'}</p>
                        </div>
                    </div>

                    <div className='intermediary-actions'>
                        <button className='intermediary-profile-action' type='button' onClick={() => setProfileModalOpen(true)}>
                            <FontAwesomeIcon icon={faUserPen} />
                            Perfil
                        </button>
                        <button className='intermediary-primary-action' type='button' onClick={openModalRegisterIntermediary} id='btn-recommendation'>
                            <FontAwesomeIcon icon={faPlus} />
                            Novo lançamento
                        </button>
                        <button className='intermediary-exit' type='button' onClick={exit}>Sair</button>
                    </div>
                </div>
                <AddRecommendationIntermediary
                    isOpen={modalIntermediaryOpen}
                    closeModal={closeModalRegisterPPR}
                    acquisitionToEdit={editAcquisition}
                />

                {loading && (
                    <div className='w-full bg-white h-64 flex items-center justify-center'>
                        <span className='text-slate-500'>Carregando intermediacoes...</span>
                    </div>
                )}

                {!loading && data.length > 0 && (
                    <div className='intermediary-list' id='table-leads'>
                        {data.map((acquisition) => {
                            const firstQuota = getFirstQuota(acquisition);
                            const document = acquisition.doc_type === 'CNPJ'
                                ? acquisition.cnpj_number
                                : acquisition.cpf_number;
                            const displayName = acquisition.doc_type === 'CNPJ'
                                ? acquisition.bussiness_name
                                : acquisition.name;
                            const isExpanded = expandedAcquisitionId === acquisition.id;

                            return (
                                <div className='intermediary-item' key={acquisition.id}>
                                    <div className='intermediary-row'>
                                        <div className='intermediary-main'>
                                            <span className='intermediary-protocol'>#{acquisition.id}</span>
                                            <strong>{displayName || '-'}</strong>
                                            <span>{document || '-'}</span>
                                        </div>

                                        <div className='intermediary-meta'>
                                            <div>
                                                <small>Valor</small>
                                                <span>{formatCurrency(acquisition.customer_value || firstQuota.credit_value)}</span>
                                            </div>
                                            <div>
                                                <small>Data</small>
                                                <span>{formatDate(acquisition.created_at)}</span>
                                            </div>
                                            <div>
                                                <small>Status</small>
                                                <span className='intermediary-status'>{formatStatus(acquisition.status)}</span>
                                            </div>
                                        </div>

                                        <div className='intermediary-row-actions'>
                                            <button type='button' className='intermediary-details-button' onClick={() => setDetailsAcquisition(acquisition)} aria-label='Ver detalhes da intermediação' title='Visualizar todos os detalhes deste lançamento'>
                                                <FontAwesomeIcon icon={faEye} />
                                            </button>
                                            <button type='button' className='intermediary-edit-button' onClick={() => openEditModal(acquisition)} aria-label='Editar intermediação' title='Editar os dados deste lançamento'>
                                                <FontAwesomeIcon icon={faPenToSquare} />
                                            </button>
                                            <button type='button' className='intermediary-document-button' onClick={() => setDocumentAcquisition(acquisition)} aria-label='Ver documentos da intermediação' title='Visualizar e gerar os documentos deste lançamento'>
                                                <FontAwesomeIcon icon={faFileLines} />
                                                <span>Documentos</span>
                                            </button>
                                            <button type='button' className='intermediary-quota-toggle' onClick={() => toggleQuotas(acquisition.id)} title={isExpanded ? 'Ocultar as cotas deste lançamento' : 'Visualizar as cotas deste lançamento'}>
                                                {isExpanded ? 'Ocultar' : 'Ver cotas'}
                                                <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                                            </button>
                                        </div>
                                    </div>

                                    {isExpanded && (
                                        <div className='intermediary-quotas'>
                                            {acquisition.quotas?.length ? (
                                                <table className='intermediary-quotas-table'>
                                                    <thead>
                                                        <tr>
                                                            <th>Grupo</th>
                                                            <th>Cota</th>
                                                            <th>Contrato</th>
                                                            <th>Credito</th>
                                                            <th>% pago</th>
                                                            <th>Recebivel</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {acquisition.quotas.map((quota) => (
                                                            <tr key={quota.id}>
                                                                <td>{quota.group_number || '-'}</td>
                                                                <td>{quota.quota_number || '-'}</td>
                                                                <td>{quota.contract_number || '-'}</td>
                                                                <td>{formatCurrency(quota.credit_value)}</td>
                                                                <td>{quota.paid_percentage_value || '-'}</td>
                                                                <td>{formatCurrency(quota.receivable_value)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            ) : (
                                                <span className='intermediary-empty-quotas'>Nenhuma cota cadastrada.</span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {!loading && data.length === 0 && (
                    <div id='first-recommendation'>
                        <h3 className='first-recommendation-title'>Ainda não possui nenhum lançamento...</h3>
                        <br />
                        <h1 className='first-recommendation-subtitle'>Faça seu primeiro lançamento</h1>
                        <button className='btn-first-add' onClick={openModalRegisterIntermediary} id='btn-first-recommendation'>Iniciar</button>
                    </div>
                )}

                <IntermediaryDetailsModal
                    acquisition={detailsAcquisition}
                    onClose={closeDetailsModal}
                />
                <IntermediaryDocumentModal
                    acquisition={documentAcquisition}
                    onClose={closeDocumentModal}
                />
                <IntermediaryProfileModal
                    isOpen={profileModalOpen}
                    consultantId={loggedIntermediaryId}
                    onClose={() => setProfileModalOpen(false)}
                    onProfileUpdated={handleProfileUpdated}
                />
            </div>
        </div>
    );
}
